/**
 * Format seconds into a human-readable time string (H:MM:SS or M:SS).
 * Extracted from VideoPlayer.vue to be shared across components.
 * When `forceHours` is true the H:MM:SS format is used even for values under one hour.
 */
export function formatSeconds(totalSeconds: number, forceHours = false): string {
	const total = Math.floor(Math.max(0, totalSeconds || 0));
	const hoursNum = Math.floor(total / 3600);
	const minutesNum = Math.floor((total % 3600) / 60);
	const secondsNum = total % 60;
	const hours = hoursNum.toString();
	const minutes = minutesNum.toString();
	const seconds = secondsNum.toString();
	if (forceHours || hoursNum > 0) {
		return `${hours}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
	}
	return `${minutes}:${seconds.padStart(2, "0")}`;
}

/**
 * Format seconds with millisecond precision (H:MM:SS.mmm or M:SS.mmm).
 * Useful for timeline scrubbing and fine-grained clip selection feedback.
 */
export function formatSecondsPrecise(totalSeconds: number, forceHours = false): string {
	const safe = Math.max(0, Number.isFinite(totalSeconds) ? totalSeconds : 0);
	const wholeSeconds = Math.floor(safe);
	const milliseconds = Math.floor((safe - wholeSeconds) * 1000);
	const hoursNum = Math.floor(wholeSeconds / 3600);
	const minutesNum = Math.floor((wholeSeconds % 3600) / 60);
	const secondsNum = wholeSeconds % 60;

	if (forceHours || hoursNum > 0) {
		return `${hoursNum}:${minutesNum.toString().padStart(2, "0")}:${secondsNum
			.toString()
			.padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
	}

	return `${minutesNum}:${secondsNum.toString().padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
}

/**
 * Format a duration in seconds into a short display string (e.g. "9s", "1m 30s").
 */
/**
 * Format seconds into a human-readable delay string (e.g. "2 minutes", "1 minute", "30 seconds").
 */
export function formatDelayHuman(seconds: number): string {
	const safeSeconds = Math.max(0, seconds);
	const minutes = Math.round(safeSeconds / 60);
	if (minutes < 1) {
		return `${safeSeconds} second${safeSeconds === 1 ? "" : "s"}`;
	}
	return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export function formatDurationShort(seconds: number): string {
	const total = Math.floor(Math.max(0, seconds || 0));
	if (total < 60) {
		return `${total}s`;
	}
	const mins = Math.floor(total / 60);
	const secs = total % 60;
	if (secs === 0) {
		return `${mins}m`;
	}
	return `${mins}m ${secs}s`;
}

/**
 * Format elapsed async-conversation time using at most two units.
 * Values are floored to one minute and intentionally remain week-based forever;
 * this copy must never escalate to months or years. The caller supplies real-elapsed
 * minutes rather than calendar units, so DST-spanning windows intentionally retain
 * real-elapsed (DST-agnostic) arithmetic.
 */
export function formatAsyncReminderElapsed(totalMinutes: number): string {
	const normalizedMinutes = Math.max(1, Math.floor(Number.isFinite(totalMinutes) ? totalMinutes : 1));
	if (normalizedMinutes < 60) {
		return `${normalizedMinutes} ${normalizedMinutes === 1 ? "minute" : "minutes"}`;
	}

	if (normalizedMinutes < 24 * 60) {
		const hours = Math.floor(normalizedMinutes / 60);
		const minutes = normalizedMinutes % 60;
		const hourLabel = `${hours} ${hours === 1 ? "hour" : "hours"}`;
		if (minutes === 0) {
			return hourLabel;
		}
		return `${hourLabel} and ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
	}

	if (normalizedMinutes < 7 * 24 * 60) {
		const days = Math.floor(normalizedMinutes / (24 * 60));
		const hours = Math.floor((normalizedMinutes % (24 * 60)) / 60);
		const dayLabel = `${days} ${days === 1 ? "day" : "days"}`;
		if (hours === 0) {
			return dayLabel;
		}
		return `${dayLabel} and ${hours} ${hours === 1 ? "hour" : "hours"}`;
	}

	const weeks = Math.floor(normalizedMinutes / (7 * 24 * 60));
	const days = Math.floor((normalizedMinutes % (7 * 24 * 60)) / (24 * 60));
	const weekLabel = `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
	if (days === 0) {
		return weekLabel;
	}
	return `${weekLabel} and ${days} ${days === 1 ? "day" : "days"}`;
}
